import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import api from '../../api/client';
import toast from 'react-hot-toast';
import StatusBadge from '../../components/shared/StatusBadge';
import PipelineStage, { PipelineTracker } from '../../components/shared/PipelineStage';
import EmployeeFile from '../../components/EmployeeFile';
import DiscProfile from '../../components/shared/DiscProfile';

// True when the stored scores object includes any non-zero DISC
// dimension — i.e., the staff took the assessment after migration 042
// added the 12 DISC questions. Older completions return false so we
// don't render an empty DISC card.
function hasDiscData(scores) {
  if (!scores) return false;
  return Math.abs(scores.D || 0) + Math.abs(scores.I || 0)
       + Math.abs(scores.S || 0) + Math.abs(scores.C || 0) > 0;
}
import { useAuth } from '../../context/AuthContext';
import {
  ArrowLeftIcon, ArrowRightIcon, EnvelopeIcon, PhoneIcon, CalendarDaysIcon, StarIcon,
  DocumentTextIcon, ClipboardDocumentListIcon, ChatBubbleLeftRightIcon,
  UserGroupIcon, ShieldCheckIcon, FolderOpenIcon, BanknotesIcon,
  PaperAirplaneIcon, TrashIcon, PlusIcon, CheckCircleIcon, ExclamationTriangleIcon,
  MicrophoneIcon, StopIcon, PauseIcon, PlayIcon, SparklesIcon, PencilSquareIcon, XMarkIcon,
  ChevronRightIcon, NoSymbolIcon,
} from '@heroicons/react/24/outline';
import { DATE_FLOOR, completionMax, expiryMax } from '../../lib/dateBounds';

const formatRole = (r) => ({
  admin: 'Office Admin',
  teacher: 'Full-Time Teacher',
  float: 'Part-Time Float',
  cook: 'Cook',
}[r] || r);

const TALENT_PROFILES = {
  stabilizer: { name: 'Reliable Stabilizer',   blurb: 'Calm, consistent, follows through. Strong on structure and predictability.', color: 'bg-blue-500' },
  caregiver:  { name: 'Compassionate Caregiver', blurb: 'Emotionally attuned, listens first, prioritises harmony and connection.', color: 'bg-pink-500' },
  leader:     { name: 'Emerging Leader',       blurb: 'Decisive, direct, takes initiative and ownership.', color: 'bg-amber-500' },
  support:    { name: 'High Support Needed',   blurb: 'Defers to leadership and external structure. Likely needs strong onboarding and direct supervision.', color: 'bg-gray-500' },
  policy:     { name: 'Policy Risk',           blurb: 'Tendency to bend rules / treat process as optional.', color: 'bg-red-500' },
  reactivity: { name: 'Emotional Reactivity Risk', blurb: 'Pattern of withdrawal or frustration under stress.', color: 'bg-red-500' },
};

function renderSurveyAnswer(r) {
  if (r?.question_type === 'forced_choice' && r.answer?.most && r.answer?.least) {
    return (
      <span>
        <span className="text-green-700">Most: {r.answer.most.text}</span>
        <span className="text-gray-400 mx-2">·</span>
        <span className="text-red-700">Least: {r.answer.least.text}</span>
      </span>
    );
  }
  if (r?.question_type === 'radio' && r.answer && typeof r.answer === 'object') {
    return <span>{r.answer.text}</span>;
  }
  if (r?.question_type === 'ranking' && Array.isArray(r.answer)) {
    return (
      <ol className="list-decimal list-inside space-y-0.5">
        {r.answer.map((opt, i) => (
          <li key={i} className="text-sm">{typeof opt === 'string' ? opt : opt?.text}</li>
        ))}
      </ol>
    );
  }
  if (r?.question_type === 'likert' && typeof r.answer === 'number') {
    return <span>{r.answer} / 5</span>;
  }
  if (Array.isArray(r?.answer)) return r.answer.join(', ');
  if (r?.answer && typeof r.answer === 'object') return r.answer.text || '—';
  return r?.answer || '—';
}
const formatStage = (s) => s?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
const formatDate = (d) => d ? new Date(d).toLocaleDateString() : '—';
const formatDateTime = (d) => d ? new Date(d).toLocaleString() : '—';

const RECOMMENDATION_LABELS = {
  strong_yes: '★★ Strong Yes', yes: '★ Yes', neutral: '— Neutral', no: '✕ No', strong_no: '✕✕ Strong No',
};
const COMPETENCIES = [
  { key: 'communication', label: 'Communication' },
  { key: 'experience', label: 'Experience' },
  { key: 'childcare_passion', label: 'Childcare Passion' },
  { key: 'reliability', label: 'Reliability' },
  { key: 'cultural_fit', label: 'Cultural Fit' },
  { key: 'professionalism', label: 'Professionalism' },
];
const NOTE_CATEGORIES = [
  { val: 'general', label: 'General', color: 'gray' },
  { val: 'interview', label: 'Interview', color: 'purple' },
  { val: 'communication', label: 'Communication', color: 'blue' },
  { val: 'reference', label: 'Reference', color: 'amber' },
  { val: 'background_check', label: 'Background Check', color: 'red' },
  { val: 'decision', label: 'Decision', color: 'green' },
];

export default function CandidateDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDirector } = useAuth();
  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('overview');
  const [editing, setEditing] = useState(false);
  const [voiding, setVoiding] = useState(false);
  const [markingNoHire, setMarkingNoHire] = useState(false);

  const markNoHire = async () => {
    if (!candidate) return;
    const reason = window.prompt(
      `Mark ${candidate.first_name} ${candidate.last_name} as No Hire?\n\n` +
      `The candidate stays on record (unlike Void) so you have the audit trail. ` +
      `They'll be moved off the active pipeline and sent the "not selected" email.\n\n` +
      `Reason (required — e.g. "did not complete drug test", "background check failed", "accepted another offer"):`
    );
    if (!reason || !reason.trim()) {
      if (reason !== null) toast.error('A reason is required.');
      return;
    }
    setMarkingNoHire(true);
    try {
      await api.post(`/recruitment/candidates/${candidate.id}/decision`, {
        decision: 'no_hire',
        notes: reason.trim(),
      });
      toast.success('Marked No Hire');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to mark No Hire');
    } finally { setMarkingNoHire(false); }
  };

  const voidCandidate = async () => {
    if (!candidate) return;
    const expected = `${candidate.first_name} ${candidate.last_name}`.trim();
    const typed = window.prompt(
      `This will PERMANENTLY DELETE ${expected} and any portal account created for them. Type their full name to confirm:`
    );
    if (!typed || typed.trim().toLowerCase() !== expected.toLowerCase()) {
      if (typed !== null) toast.error('Name did not match — void cancelled.');
      return;
    }
    setVoiding(true);
    try {
      const { data } = await api.delete(`/recruitment/candidates/${candidate.id}/void`);
      toast.success(data.message || 'Candidate voided');
      navigate('/recruitment');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to void candidate');
    } finally { setVoiding(false); }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/recruitment/candidates/${id}`);
      setCandidate(data);
    } catch {
      toast.error('Failed to load candidate');
      navigate('/recruitment');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  if (loading) return (
    <div className="flex justify-center py-12">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" />
    </div>
  );
  if (!candidate) return null;

  const baseTabs = [
    { id: 'overview', label: 'Overview', icon: FolderOpenIcon },
    { id: 'interview', label: 'Interview', icon: ClipboardDocumentListIcon },
    { id: 'references', label: 'References', icon: UserGroupIcon },
    { id: 'documents', label: 'Employment File', icon: DocumentTextIcon },
    { id: 'background', label: 'Background', icon: ShieldCheckIcon },
    { id: 'offer', label: 'Offer / Negotiate', icon: BanknotesIcon },
  ];
  // The Clearance tab only appears once we've moved this candidate forward —
  // before that there's nothing to act on. Slotted between Offer and Notes.
  const tabs = candidate.move_forward_at
    ? [...baseTabs, { id: 'clearance', label: 'Clearance & Onboarding', icon: ShieldCheckIcon },
       { id: 'notes', label: 'Notes', icon: ChatBubbleLeftRightIcon }]
    : [...baseTabs, { id: 'notes', label: 'Notes', icon: ChatBubbleLeftRightIcon }];

  return (
    <div className="animate-slide-up">
      {/* Back + Header */}
      <div className="mb-6">
        <Link to="/recruitment" className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-orange-500 mb-3">
          <ArrowLeftIcon className="w-4 h-4" /> Back to Pipeline
        </Link>
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm">
          <div className="flex items-start justify-between gap-4">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">{candidate.first_name} {candidate.last_name}</h1>
              <p className="text-sm text-gray-500 mt-1">Applied {formatDate(candidate.created_at)} · {formatRole(candidate.role_applied)}</p>
              <div className="flex flex-wrap gap-4 mt-3 text-sm">
                <span className="inline-flex items-center gap-1 text-gray-600">
                  <EnvelopeIcon className="w-4 h-4" /> {candidate.email}
                </span>
                {candidate.phone && (
                  <span className="inline-flex items-center gap-1 text-gray-600">
                    <PhoneIcon className="w-4 h-4" /> {candidate.phone}
                  </span>
                )}
                {candidate.source_detail && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-violet-50 text-violet-700">
                    Source: {candidate.source_detail}
                  </span>
                )}
              </div>
            </div>
            <div className="text-right">
              <PipelineStage stage={candidate.pipeline_stage} size="lg" />
              {candidate.interview_score && (
                <div className="flex items-center justify-end gap-1 mt-2">
                  <StarIcon className="w-4 h-4 text-amber-500" />
                  <span className="text-sm font-medium">{candidate.interview_score}/5</span>
                </div>
              )}
              <div className="mt-3 flex items-center gap-2 justify-end">
                <button onClick={() => setEditing(true)}
                  className="inline-flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-orange-600 border border-gray-200 hover:border-orange-300 rounded-lg px-2.5 py-1">
                  <PencilSquareIcon className="w-3.5 h-3.5" /> Edit profile
                </button>
                {candidate.portal_user_id && (
                  <CandidateHeaderNudgeButton userId={candidate.portal_user_id} />
                )}
                {!['hired', 'no_hire', 'talent_pool'].includes(candidate.stage) && (
                  <button onClick={markNoHire} disabled={markingNoHire}
                    title="Record why this candidate is not moving forward. Keeps them on record for audit (use Void to delete entirely)."
                    className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 hover:text-amber-800 border border-amber-200 hover:border-amber-400 rounded-lg px-2.5 py-1 disabled:opacity-40">
                    <NoSymbolIcon className="w-3.5 h-3.5" />
                    {markingNoHire ? 'Marking…' : 'Mark No Hire'}
                  </button>
                )}
                {isDirector && (
                  <button onClick={voidCandidate} disabled={voiding}
                    title="Director-only: permanently delete this candidate + any portal account."
                    className="inline-flex items-center gap-1 text-xs font-medium text-red-600 hover:text-red-700 border border-red-200 hover:border-red-400 rounded-lg px-2.5 py-1 disabled:opacity-40">
                    <TrashIcon className="w-3.5 h-3.5" />
                    {voiding ? 'Voiding…' : 'Void'}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pipeline progress tracker — shows where this person sits in the
          unified Applied → Active journey across Recruitment + Staff. */}
      <div className="mb-4">
        <PipelineTracker stage={candidate.pipeline_stage} />
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 overflow-x-auto pb-1 border-b border-gray-200">
        {tabs.map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                tab === t.id ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {tab === 'overview' && <OverviewTab candidate={candidate} reload={load} />}
      {tab === 'interview' && <InterviewTab candidate={candidate} reload={load} />}
      {tab === 'references' && <ReferencesTab candidate={candidate} reload={load} />}
      {tab === 'documents' && <DocumentsTab candidate={candidate} reload={load} />}
      {tab === 'background' && <BackgroundTab candidate={candidate} reload={load} />}
      {tab === 'offer' && <OfferTab candidate={candidate} reload={load} />}
      {tab === 'clearance' && <ClearanceTab candidate={candidate} reload={load} />}
      {tab === 'notes' && <NotesTab candidate={candidate} reload={load} />}

      {editing && (
        <EditProfileModal
          candidate={candidate}
          onClose={() => setEditing(false)}
          onSaved={() => { setEditing(false); load(); }}
        />
      )}
    </div>
  );
}

// ─── Edit Profile Modal ─────────────────────────────────────
// Mirrors the Add Candidate form on RecruitmentPage, plus stage. Lets admin
// fill in fields they skipped at intake (everything except resume + email
// uniqueness are handled here).
function EditProfileModal({ candidate, onClose, onSaved }) {
  const { register, handleSubmit, formState: { isSubmitting } } = useForm({
    defaultValues: {
      first_name: candidate.first_name || '',
      last_name: candidate.last_name || '',
      email: candidate.email || '',
      phone: candidate.phone || '',
      role_applied: candidate.role_applied || '',
      stage: candidate.stage || 'application_received',
      source_detail: candidate.source_detail || '',
      referred_by: candidate.referred_by || '',
      availability_days: candidate.availability_days || '',
      availability_hours: candidate.availability_hours || '',
      earliest_start_date: candidate.earliest_start_date ? candidate.earliest_start_date.slice(0, 10) : '',
      date_of_birth: candidate.date_of_birth ? candidate.date_of_birth.slice(0, 10) : '',
      state_of_birth: candidate.state_of_birth || '',
    },
  });

  const onSubmit = async (data) => {
    // Empty strings → null so we don't store "" in date / nullable columns
    const cleaned = Object.fromEntries(
      Object.entries(data).map(([k, v]) => [k, v === '' ? null : v])
    );
    try {
      await api.patch(`/recruitment/candidates/${candidate.id}`, cleaned);
      toast.success('Profile updated');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <h2 className="text-xl font-bold text-gray-900">Edit candidate profile</h2>
          <button onClick={onClose}><XMarkIcon className="w-5 h-5 text-gray-400 hover:text-gray-600" /></button>
        </div>
        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">First name</label>
              <input {...register('first_name', { required: true })}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
              <input {...register('last_name', { required: true })}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input type="email" {...register('email', { required: true })}
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input {...register('phone')}
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Role applied</label>
              <select {...register('role_applied', { required: true })}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 bg-white focus:border-orange-500 focus:outline-none">
                <option value="admin">Office Admin</option>
                <option value="teacher">Full-Time Teacher</option>
                <option value="float">Part-Time Float</option>
                <option value="cook">Cook</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Pipeline stage</label>
              <select {...register('stage', { required: true })}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 bg-white focus:border-orange-500 focus:outline-none">
                {['application_received','pre_interview','interview_scheduled','interview_conducted','hire_decision','offer_letter','hired','no_hire','talent_pool'].map(s => (
                  <option key={s} value={s}>{s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">How they found us</label>
              <select {...register('source_detail')}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 bg-white focus:border-orange-500 focus:outline-none">
                <option value="">—</option>
                <option value="indeed">Indeed</option>
                <option value="referral">Referral</option>
                <option value="walk_in">Walk-in</option>
                <option value="website">Website</option>
                <option value="other">Other</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Referred by</label>
              <input {...register('referred_by')} placeholder="Name (if referral)"
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Available days</label>
            <input {...register('availability_days')} placeholder="monday,tuesday,wednesday"
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Available hours</label>
              <input {...register('availability_hours')} placeholder="7am-3pm"
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Earliest start date</label>
              <input type="date" min={DATE_FLOOR} {...register('earliest_start_date')}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of birth</label>
              <input type="date" min={DATE_FLOOR} {...register('date_of_birth')}
                className="w-full rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">State of birth</label>
              <input {...register('state_of_birth')} maxLength={2} placeholder="IN"
                className="w-full uppercase rounded-xl border border-gray-200 px-3 py-2.5 focus:border-orange-500 focus:outline-none" />
            </div>
          </div>
          <p className="text-[11px] text-gray-500 -mt-2">
            DOB + state of birth gate iLEAD consent, background check, and drug screen scheduling.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose}
              className="px-4 py-2.5 border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50">Cancel</button>
            <button type="submit" disabled={isSubmitting}
              className="px-5 py-2.5 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/40 text-white rounded-xl font-semibold">
              {isSubmitting ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Overview Tab ───────────────────────────────────────────
function OverviewTab({ candidate, reload }) {
  const [sending, setSending] = useState(false);

  const sendAssessmentLink = async () => {
    if (candidate.survey_sent_at && !window.confirm(
      'Send a fresh assessment link? The previous link will stop working.'
    )) return;
    setSending(true);
    try {
      await api.post(`/recruitment/candidates/${candidate.id}/send-survey`);
      toast.success(candidate.survey_sent_at ? 'Fresh link sent' : 'Assessment link sent');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send link');
    } finally { setSending(false); }
  };

  const assessmentDone = !!candidate.survey_completed_at;
  const assessmentSent = !!candidate.survey_sent_at;
  const assessmentClosed = ['hired', 'no_hire', 'talent_pool'].includes(candidate.stage);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {!assessmentDone && !assessmentClosed && (
        <Card title="Talent Assessment" icon={ClipboardDocumentListIcon} className="lg:col-span-2">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-sm text-gray-600">
              {assessmentSent ? (
                <>Link last sent <strong>{formatDateTime(candidate.survey_sent_at)}</strong>. Candidate hasn't completed the assessment yet.</>
              ) : (
                <>Send the candidate an email with the attestation (18+, drug, CPS) and Talent &amp; Culture Assessment.</>
              )}
            </div>
            <button onClick={sendAssessmentLink} disabled={sending}
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/40 text-white text-sm font-semibold px-4 py-2 rounded-lg whitespace-nowrap">
              <PaperAirplaneIcon className="w-4 h-4" />
              {sending ? 'Sending…' : assessmentSent ? 'Re-send fresh link' : 'Send assessment link'}
            </button>
          </div>
        </Card>
      )}

      <Card title="Contact" icon={EnvelopeIcon}>
        <Field label="Email" value={candidate.email} />
        <Field label="Phone" value={candidate.phone} />
        <Field label="Role Applied" value={formatRole(candidate.role_applied)} />
        {candidate.referred_by && <Field label="Referred By" value={candidate.referred_by} />}
      </Card>

      <Card title="Availability" icon={CalendarDaysIcon}>
        <Field label="Days" value={candidate.availability_days || '—'} />
        <Field label="Hours" value={candidate.availability_hours || '—'} />
        <Field label="Earliest Start" value={formatDate(candidate.earliest_start_date)} />
      </Card>

      <Card title="Source" icon={UserGroupIcon}>
        <Field label="How They Came In" value={candidate.source === 'public_application' ? 'Online Application' : 'Admin Entry'} />
        <Field label="Detail" value={candidate.source_detail || '—'} />
      </Card>

      <Card title="Resume" icon={DocumentTextIcon}>
        {candidate.resume_url ? (
          <a href={candidate.resume_url} target="_blank" rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-orange-600 hover:underline text-sm font-medium">
            <DocumentTextIcon className="w-5 h-5" /> View Resume
          </a>
        ) : (
          <p className="text-sm text-gray-400">No resume uploaded</p>
        )}
      </Card>

      {candidate.survey_completed_at && candidate.talent_profile && (
        <Card title="Talent Profile" icon={SparklesIcon} className="lg:col-span-2">
          <div className="grid md:grid-cols-[1fr_2fr] gap-6">
            <div>
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">Primary profile</div>
              <div className="text-2xl font-bold text-gray-900">
                {TALENT_PROFILES[candidate.talent_profile]?.name}
              </div>
              <p className="text-sm text-gray-600 mt-2">
                {TALENT_PROFILES[candidate.talent_profile]?.blurb}
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                {candidate.risk_policy && (
                  <span className="inline-flex items-center gap-1 bg-red-50 border border-red-200 text-red-700 text-xs font-medium px-2.5 py-1 rounded-full">
                    <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                    Policy Risk
                  </span>
                )}
                {candidate.risk_emotional_reactivity && (
                  <span className="inline-flex items-center gap-1 bg-red-50 border border-red-200 text-red-700 text-xs font-medium px-2.5 py-1 rounded-full">
                    <ExclamationTriangleIcon className="w-3.5 h-3.5" />
                    Emotional Reactivity Risk
                  </span>
                )}
                {candidate.consistency_flag > 0 && (
                  <span className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-700 text-xs font-medium px-2.5 py-1 rounded-full">
                    Consistency: {candidate.consistency_flag} contradiction{candidate.consistency_flag === 1 ? '' : 's'}
                  </span>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <div className="text-xs text-gray-500 uppercase tracking-wide mb-1">All scores</div>
              {['stabilizer', 'caregiver', 'leader', 'support', 'policy', 'reactivity'].map(profile => {
                const raw = candidate.talent_profile_scores?.[profile] ?? 0;
                // Scores can dip negative on forced-choice. Map [-6, 24] → [0, 100].
                const widthPct = Math.max(0, Math.min(100, Math.round(((raw + 6) / 30) * 100)));
                const isRisk = profile === 'policy' || profile === 'reactivity';
                return (
                  <div key={profile}>
                    <div className="flex items-center justify-between text-xs mb-0.5">
                      <span className={`font-medium ${isRisk ? 'text-red-700' : 'text-gray-700'}`}>
                        {TALENT_PROFILES[profile]?.name}
                      </span>
                      <span className="text-gray-500">{raw > 0 ? `+${raw}` : raw}</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div
                        className={`h-full ${TALENT_PROFILES[profile]?.color || 'bg-gray-400'} transition-all`}
                        style={{ width: `${widthPct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </Card>
      )}

      {candidate.survey_completed_at && hasDiscData(candidate.talent_profile_scores) && (
        <Card title="DISC Profile" icon={SparklesIcon} className="lg:col-span-2">
          <DiscProfile scores={candidate.talent_profile_scores} />
        </Card>
      )}

      {candidate.survey_completed_at && (
        <Card title="Questionnaire Responses" icon={ClipboardDocumentListIcon} className="lg:col-span-2">
          <div className="space-y-3 max-h-80 overflow-y-auto">
            {(Array.isArray(candidate.survey_responses) ? candidate.survey_responses : []).map((r, i) => (
              <div key={i} className="border-l-2 border-orange-200 pl-3">
                <p className="text-xs text-gray-500">{r.question_text}</p>
                <p className="text-sm text-gray-900 mt-0.5">{renderSurveyAnswer(r)}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {candidate.email_log?.length > 0 && (
        <Card title="Email History" icon={EnvelopeIcon} className="lg:col-span-2">
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {candidate.email_log.map(e => (
              <div key={e.id} className="flex items-center justify-between py-1.5 text-sm">
                <div>
                  <span className="font-medium text-gray-900">{formatStage(e.template_name)}</span>
                  <span className="text-gray-400 ml-2 text-xs">{formatDateTime(e.created_at)}</span>
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${e.status === 'sent' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                  {e.status}
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {candidate.tasks?.length > 0 && (
        <Card title="Follow-up Tasks" icon={CheckCircleIcon} className="lg:col-span-2">
          <div className="space-y-2">
            {candidate.tasks.map(t => (
              <CandidateTaskRow key={t.id} task={t} />
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// One task row inside the candidate file's Follow-up Tasks card.
const TASK_STATUS_STYLES = {
  open: 'bg-gray-100 text-gray-600',
  in_progress: 'bg-blue-100 text-blue-700',
  pending_verification: 'bg-amber-100 text-amber-700',
  complete: 'bg-green-100 text-green-700',
};
const TASK_STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In Progress',
  pending_verification: 'Pending Verification',
  complete: 'Complete',
};
function CandidateTaskRow({ task }) {
  const isComplete = task.status === 'complete';
  return (
    <div className={`rounded-xl border p-3 ${isComplete ? 'border-green-100 bg-green-50/30' : 'border-gray-100 bg-white'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 flex items-center gap-2">
            <span className="truncate">{task.title}</span>
            {task.auto_source === 'ai_interview_followup' && (
              <span className="shrink-0 inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-orange-50 text-orange-600">
                <SparklesIcon className="w-3 h-3" /> From interview
              </span>
            )}
          </p>
          <p className="text-[11px] text-gray-400 mt-0.5">
            {task.area && <span className="capitalize">{task.area}</span>}
            {task.assigned_to_name && <span> · {task.assigned_to_name}</span>}
            {task.due_date && !isComplete && <span> · Due {formatDate(task.due_date)}</span>}
          </p>
        </div>
        <span className={`shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${TASK_STATUS_STYLES[task.status] || 'bg-gray-100 text-gray-600'}`}>
          {TASK_STATUS_LABELS[task.status] || task.status}
        </span>
      </div>
      {isComplete && (
        <div className="mt-2 pt-2 border-t border-green-100 text-xs">
          <p className="text-gray-500">
            Completed{task.completed_by_name && <> by <strong className="text-gray-700">{task.completed_by_name}</strong></>}
            {task.completed_at && <> · {formatDateTime(task.completed_at)}</>}
          </p>
          {task.notes && (
            <p className="text-sm text-gray-700 mt-1 whitespace-pre-wrap">{task.notes}</p>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Interview Tab ──────────────────────────────────────────
function InterviewTab({ candidate, reload }) {
  const { user: me } = useAuth();
  const [interviews, setInterviews] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [bank, setBank] = useState([]);

  const loadInterviews = useCallback(async () => {
    try {
      const { data } = await api.get(`/recruitment/candidates/${candidate.id}/interviews`);
      setInterviews(data);
      // Pick the highest round_number as the default active.
      if (data.length > 0 && (activeId == null || !data.find(i => i.id === activeId))) {
        setActiveId(data[data.length - 1].id);
      }
      // Auto-create Round 1 if the candidate has none yet (new candidate path).
      if (data.length === 0) {
        setCreating(true);
        try {
          const { data: newRound } = await api.post(
            `/recruitment/candidates/${candidate.id}/interviews`,
            { title: 'Interview', seed_questions: true }
          );
          setInterviews([newRound]);
          setActiveId(newRound.id);
        } finally { setCreating(false); }
      }
    } catch (err) {
      toast.error('Failed to load interviews');
    } finally { setLoading(false); }
  }, [candidate.id]); // eslint-disable-line

  useEffect(() => { loadInterviews(); }, [loadInterviews]);

  useEffect(() => {
    api.get('/recruitment/question-bank', { params: { role: candidate.role_applied } })
      .then(({ data }) => setBank(data))
      .catch(() => {});
  }, [candidate.role_applied]);

  const addRound = async () => {
    setCreating(true);
    try {
      const next = (interviews[interviews.length - 1]?.round_number || 0) + 1;
      const { data: newRound } = await api.post(
        `/recruitment/candidates/${candidate.id}/interviews`,
        { title: `Round ${next}`, seed_questions: true }
      );
      toast.success(`${newRound.title} started`);
      await loadInterviews();
      setActiveId(newRound.id);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add round');
    } finally { setCreating(false); }
  };

  const deleteRound = async (roundId) => {
    if (!confirm('Delete this entire interview round (recordings, questions, scorecards)? This cannot be undone.')) return;
    try {
      await api.delete(`/recruitment/interviews/${roundId}`);
      toast.success('Round deleted');
      if (activeId === roundId) setActiveId(null);
      await loadInterviews();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete');
    }
  };

  const renameRound = async (roundId, title) => {
    try {
      await api.patch(`/recruitment/interviews/${roundId}`, { title });
      await loadInterviews();
    } catch { toast.error('Rename failed'); }
  };

  // Open/closed state per round. Most recent round opens by default; older
  // rounds collapse to a one-line summary you click to expand.
  const [openIds, setOpenIds] = useState(new Set());
  useEffect(() => {
    if (interviews.length > 0) {
      // Default: latest round open, older ones closed.
      const latest = interviews[interviews.length - 1].id;
      setOpenIds(new Set([latest]));
    }
  }, [interviews.length]); // eslint-disable-line

  const toggle = (id) => {
    setOpenIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  if (loading || creating) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>;
  }

  const isLocked = !!candidate.hire_decision;
  const isDirector = me?.role === 'director';

  return (
    <div className="space-y-3">
      {/* Lock banner when a hiring decision has been made */}
      {isLocked && (
        <div className="bg-gray-100 border border-gray-300 rounded-xl px-4 py-3 flex items-start gap-3">
          <ExclamationTriangleIcon className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-gray-700 flex-1">
            <p className="font-semibold">
              Interview locked — hiring decision: <span className="text-gray-900">{candidate.hire_decision.replace(/_/g, ' ')}</span>
            </p>
            <p className="text-xs text-gray-500 mt-0.5">
              {isDirector
                ? 'You can still edit as director, but everyone else is read-only. The interview record reflects what was decided on.'
                : 'Notes, scorecards, and recordings are read-only. Ask the director if a change needs to be made.'}
            </p>
          </div>
        </div>
      )}

      {/* Vertical list of interview rounds — accordion style */}
      {interviews.map((iv, idx) => (
        <InterviewRoundCard
          key={iv.id}
          interview={iv}
          isOpen={openIds.has(iv.id)}
          onToggle={() => toggle(iv.id)}
          candidate={candidate}
          bank={bank}
          me={me}
          isLocked={isLocked && !isDirector}
          reload={reload}
          onInterviewsChanged={loadInterviews}
          onRename={(title) => renameRound(iv.id, title)}
          onDelete={isDirector && interviews.length > 1 ? () => deleteRound(iv.id) : null}
          orderLabel={`Meeting ${idx + 1} of ${interviews.length}`}
        />
      ))}

      {/* Add another interview meeting */}
      {!isLocked && (
        <button onClick={addRound}
          className="w-full bg-orange-50 border border-dashed border-orange-200 text-orange-600 px-4 py-3 rounded-xl text-sm font-medium hover:bg-orange-100 inline-flex items-center justify-center gap-1">
          <PlusIcon className="w-4 h-4" /> Add another interview meeting
        </button>
      )}

      {/* Quick "mark no-show" — goes through the existing recordDecision flow.
          Closes the candidate out without forcing the director to fill the
          full hire-decision form. */}
      {!isLocked && !candidate.no_show && interviews.some(i => i.scheduled_at) && (
        <button
          onClick={async () => {
            if (!window.confirm(`Mark ${candidate.first_name} ${candidate.last_name} as a no-show? This sends the no-show email and closes the candidate out.`)) return;
            try {
              await api.post(`/recruitment/candidates/${candidate.id}/decision`, {
                decision: 'no_show',
                notes: 'Marked no-show from interview tab',
              });
              toast.success('Marked as no-show');
              reload();
            } catch (err) {
              toast.error(err.response?.data?.error || 'Failed to mark no-show');
            }
          }}
          className="w-full bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-xl text-sm font-medium hover:bg-red-100 inline-flex items-center justify-center gap-1"
        >
          <ExclamationTriangleIcon className="w-4 h-4" /> Mark no-show
        </button>
      )}
      {candidate.no_show && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-2 rounded-xl text-sm text-center">
          ❌ Marked as no-show
        </div>
      )}
    </div>
  );
}

function InterviewRoundCard({
  interview, isOpen, onToggle, candidate, bank, me, isLocked,
  reload, onInterviewsChanged, onRename, onDelete, orderLabel,
}) {
  // Filter the candidate's full lists by this round's interview_id (for the summary line).
  const allQuestions = candidate.interview_question_responses || [];
  const myQuestions = allQuestions.filter(q => q.interview_id === interview.id);
  const allScorecards = candidate.scorecards || [];
  const myScorecards = allScorecards.filter(s => s.interview_id === interview.id);
  const humanScorecards = myScorecards.filter(s => !s.ai_generated);
  const aiScorecard = myScorecards.find(s => s.ai_generated);

  const summaryBits = [];
  if (interview.recording_count > 0) summaryBits.push(`🎤 ${interview.recording_count}`);
  if (humanScorecards.length > 0) summaryBits.push(`⭐ ${humanScorecards.length} scored`);
  if (aiScorecard) summaryBits.push('🤖 AI scored');
  if (myQuestions.length > 0) summaryBits.push(`${myQuestions.length} Qs`);

  return (
    <div className="bg-white rounded-2xl border border-gray-200 overflow-hidden">
      <button onClick={onToggle}
        className="w-full px-4 py-3 flex items-center gap-3 hover:bg-gray-50 transition text-left">
        <ChevronRightIcon className={`w-4 h-4 text-gray-400 flex-shrink-0 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <span className="text-sm font-semibold text-gray-900">
              {interview.title || `Round ${interview.round_number}`}
            </span>
            <span className="text-[11px] text-gray-400">{orderLabel}</span>
            {interview.scheduled_at && (
              <span className="text-[11px] text-gray-500">
                · {new Date(interview.scheduled_at).toLocaleString()}
              </span>
            )}
          </div>
          {summaryBits.length > 0 && (
            <p className="text-xs text-gray-500 mt-0.5">{summaryBits.join(' · ')}</p>
          )}
        </div>
        {onDelete && (
          <span onClick={(e) => { e.stopPropagation(); onDelete(); }}
            className="text-xs text-gray-300 hover:text-red-500 inline-flex items-center gap-1 cursor-pointer">
            <TrashIcon className="w-3.5 h-3.5" />
          </span>
        )}
      </button>

      {isOpen && (
        <div className="border-t border-gray-100 p-4">
          <RoundPanel
            candidate={candidate}
            interview={interview}
            bank={bank}
            me={me}
            isLocked={isLocked}
            reload={reload}
            onInterviewsChanged={onInterviewsChanged}
            onRename={onRename}
          />
        </div>
      )}
    </div>
  );
}

function RoundPanel({ candidate, interview, bank, me, isLocked, reload, onInterviewsChanged, onRename }) {
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [customQText, setCustomQText] = useState('');
  const [editingTitle, setEditingTitle] = useState(false);
  const [titleDraft, setTitleDraft] = useState(interview.title || `Round ${interview.round_number}`);

  // Filter the candidate's full lists by this round's interview_id.
  const allQuestions = candidate.interview_question_responses || [];
  const questions = allQuestions.filter(q => q.interview_id === interview.id);

  const allScorecards = candidate.scorecards || [];
  const scorecards = allScorecards.filter(s => s.interview_id === interview.id);
  const aiScorecard = scorecards.find(s => s.ai_generated) || null;
  const humanScorecards = scorecards.filter(s => !s.ai_generated);
  const myScorecard = humanScorecards.find(s => s.scored_by === me?.id) || null;

  const scorecardForm = useForm({ defaultValues: myScorecard || {} });

  useEffect(() => {
    scorecardForm.reset(myScorecard || {});
  }, [myScorecard?.id, scorecardForm]); // eslint-disable-line

  const removeQ = async (qId) => {
    try {
      await api.delete(`/recruitment/interview-questions/${qId}`);
      reload();
    } catch { toast.error('Failed to remove question'); }
  };

  const addCustomQ = async () => {
    const text = customQText.trim();
    if (!text) return;
    try {
      await api.post(`/recruitment/candidates/${candidate.id}/interview-questions/add`, {
        question_text: text,
        interview_id: interview.id,
      });
      setCustomQText('');
      setShowAddCustom(false);
      toast.success('Question added');
      reload();
    } catch { toast.error('Failed to add question'); }
  };

  const updateQ = async (qId, patch) => {
    try {
      await api.patch(`/recruitment/interview-questions/${qId}`, patch);
      reload();
    } catch { toast.error('Failed to save'); }
  };

  const saveScorecard = async (data) => {
    try {
      const cleaned = { ...data, interview_id: interview.id };
      for (const k of Object.keys(cleaned)) {
        if (cleaned[k] === '' || cleaned[k] === undefined) cleaned[k] = null;
        else if (COMPETENCIES.some(c => c.key === k)) cleaned[k] = parseInt(cleaned[k], 10);
      }
      await api.post(`/recruitment/candidates/${candidate.id}/scorecard`, cleaned);
      toast.success('Your scorecard saved');
      reload();
    } catch { toast.error('Failed to save scorecard'); }
  };

  const [runningAi, setRunningAi] = useState(false);
  const runAiScore = async () => {
    if (runningAi) return;
    setRunningAi(true);
    try {
      await api.post(`/recruitment/candidates/${candidate.id}/ai-score`, {
        interview_id: interview.id,
      });
      toast.success('AI baseline score generated');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.error || 'AI scoring failed');
    } finally {
      setRunningAi(false);
    }
  };

  const deleteScorecard = async (scId) => {
    if (!confirm('Delete this scorecard?')) return;
    try {
      await api.delete(`/recruitment/candidates/${candidate.id}/scorecards/${scId}`);
      toast.success('Deleted');
      reload();
    } catch { toast.error('Delete failed'); }
  };

  const saveTitle = async () => {
    const t = titleDraft.trim();
    if (!t) { setEditingTitle(false); return; }
    setEditingTitle(false);
    if (t !== interview.title) await onRename(t);
  };

  return (
    <div className="space-y-4">
      {/* Round header (rename + scheduled details) */}
      <div className="bg-white rounded-2xl border border-gray-200 px-4 py-3 flex items-center gap-3 flex-wrap">
        <span className="text-xs uppercase tracking-wide text-gray-400 font-semibold">
          Round {interview.round_number}
        </span>
        {editingTitle && !isLocked ? (
          <input value={titleDraft}
            onChange={(e) => setTitleDraft(e.target.value)}
            onBlur={saveTitle}
            onKeyDown={(e) => e.key === 'Enter' && saveTitle()}
            autoFocus
            className="rounded-lg border border-gray-200 px-2 py-0.5 text-sm" />
        ) : (
          <button onClick={() => !isLocked && setEditingTitle(true)} disabled={isLocked}
            className={`text-sm font-semibold inline-flex items-center gap-1 ${isLocked ? 'text-gray-700 cursor-default' : 'text-gray-900 hover:text-orange-500'}`}>
            {interview.title || `Round ${interview.round_number}`}
            {!isLocked && <PencilSquareIcon className="w-3.5 h-3.5 text-gray-300" />}
          </button>
        )}
        {interview.created_at && (
          <span className="text-xs text-gray-400">Started {formatDate(interview.created_at)}</span>
        )}
      </div>

      {/* Combined: Interview details + Recording (scoped to this round) */}
      <RecordingPanel candidate={candidate}
        interviewId={interview.id}
        interviewDate={interview.scheduled_at}
        zoomLink={interview.zoom_link}
        isLocked={isLocked} />

      {/* Question Kit — all bank questions seeded automatically; admin removes the ones they didn't use */}
      <Card title={`Interview Questions${questions.length ? ` (${questions.length})` : ''}`} icon={ClipboardDocumentListIcon}
        action={!isLocked && (
          <button onClick={() => setShowAddCustom(true)}
            className="text-xs bg-orange-50 text-orange-600 px-3 py-1.5 rounded-lg hover:bg-orange-100 font-medium inline-flex items-center gap-1">
            <PlusIcon className="w-3.5 h-3.5" /> Add custom
          </button>
        )}>
        {questions.length === 0 ? (
          <div className="text-sm text-gray-500">
            <p>No questions for this role's bank yet.</p>
            <Link to="/recruitment/talent-pool" className="text-orange-500 hover:underline text-xs">
              Manage the question bank →
            </Link>
          </div>
        ) : (
          <div className="space-y-2">
            <p className="text-[11px] text-gray-500 mb-2">
              All {bank.length} bank question{bank.length === 1 ? '' : 's'} for <strong>{formatRole(candidate.role_applied)}</strong>{' '}
              are below. Click ✕ on any you didn't ask.
            </p>
            {questions.map((q, i) => (
              <div key={q.id} className={`group rounded-xl px-3 py-2 transition border ${
                q.response ? 'border-amber-100 bg-amber-50/30' : 'border-gray-100 hover:border-gray-200'
              }`}>
                <div className="flex items-start gap-2">
                  <span className="text-xs font-semibold text-gray-400 mt-0.5 w-5 text-right flex-shrink-0">{i + 1}.</span>
                  <p className="text-sm text-gray-900 flex-1">{q.question_text}</p>
                  {!isLocked && (
                    <button onClick={() => removeQ(q.id)}
                      title="Remove this question (didn't ask it)"
                      className="text-gray-300 hover:text-red-500 flex-shrink-0">
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <textarea
                  defaultValue={q.response || ''}
                  onBlur={(e) => !isLocked && e.target.value !== (q.response || '') && updateQ(q.id, { response: e.target.value })}
                  placeholder={isLocked ? '—' : 'Notes on the response…'}
                  readOnly={isLocked}
                  rows={1}
                  className={`w-full mt-1 rounded-lg border border-gray-100 px-2 py-1 text-xs resize-y ${isLocked ? 'bg-gray-50 text-gray-600 cursor-default' : 'bg-white focus:border-orange-500 focus:outline-none'}`} />
                <div className="flex items-center gap-1 mt-1">
                  <span className="text-[10px] text-gray-400">Rating:</span>
                  {[1, 2, 3, 4, 5].map(n => (
                    <button key={n} onClick={() => !isLocked && updateQ(q.id, { rating: q.rating === n ? null : n })}
                      disabled={isLocked}
                      className={`w-6 h-6 rounded-full text-[10px] font-medium ${q.rating === n ? 'bg-amber-500 text-white' : 'bg-gray-100 text-gray-400 hover:bg-gray-200'} ${isLocked ? 'cursor-default opacity-70' : ''}`}>
                      {n}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {showAddCustom && (
          <div className="mt-3 p-3 rounded-xl border border-orange-200 bg-orange-50/40">
            <p className="text-xs font-medium text-gray-700 mb-1">Add a custom question</p>
            <textarea value={customQText} onChange={(e) => setCustomQText(e.target.value)}
              placeholder="Type the question…"
              rows={2}
              autoFocus
              className="w-full rounded-lg border border-gray-200 px-2 py-1 text-sm focus:border-orange-500 focus:outline-none resize-y" />
            <div className="flex justify-end gap-2 mt-2">
              <button onClick={() => { setShowAddCustom(false); setCustomQText(''); }}
                className="text-xs px-3 py-1 text-gray-500 hover:text-gray-700">Cancel</button>
              <button onClick={addCustomQ} disabled={!customQText.trim()}
                className="bg-orange-500 text-white text-xs px-3 py-1 rounded-lg hover:bg-orange-600 disabled:opacity-50">
                Add question
              </button>
            </div>
          </div>
        )}
      </Card>

      {/* AI baseline score */}
      <Card title="AI Baseline Score" icon={SparklesIcon}
        action={me?.role === 'director' && !isLocked && (
          <button onClick={runAiScore} disabled={runningAi}
            className="text-sm bg-orange-500 text-white px-3 py-1.5 rounded-lg hover:bg-orange-600 disabled:opacity-50 inline-flex items-center gap-1 font-medium">
            <SparklesIcon className="w-4 h-4" />
            {runningAi ? 'Scoring…' : aiScorecard ? 'Re-run AI Score' : 'Run AI Score'}
          </button>
        )}>
        {aiScorecard ? (
          <ScorecardReadOnly sc={aiScorecard} isAi onDelete={() => deleteScorecard(aiScorecard.id)} canDelete={me?.role === 'director' && !isLocked} />
        ) : (
          <p className="text-sm text-gray-400">
            {me?.role === 'director'
              ? 'Run an AI baseline score against typical childcare competencies. Useful before the interview to flag what to dig into.'
              : 'No AI baseline score yet — director can generate one.'}
          </p>
        )}
      </Card>

      {/* Other evaluators' scorecards */}
      <Card title={`Evaluator Scorecards${humanScorecards.length > 0 ? ` (${humanScorecards.length})` : ''}`} icon={UserGroupIcon}>
        {humanScorecards.length === 0 ? (
          <p className="text-sm text-gray-400">No interviews scored yet. Each admin can leave their own scorecard below.</p>
        ) : (
          <div className="space-y-3">
            {humanScorecards.filter(s => s.scored_by !== me?.id).map(sc => (
              <ScorecardReadOnly key={sc.id} sc={sc}
                canDelete={me?.role === 'director' && !isLocked}
                onDelete={() => deleteScorecard(sc.id)} />
            ))}
            {humanScorecards.length > 1 && (
              <SummaryRow scorecards={humanScorecards} />
            )}
          </div>
        )}
      </Card>

      {/* My scorecard — read-only view when locked, editable form otherwise */}
      <Card title={myScorecard ? 'My Scorecard' : 'Add My Scorecard'} icon={StarIcon}>
        {isLocked ? (
          myScorecard ? (
            <ScorecardReadOnly sc={myScorecard} canDelete={false} />
          ) : (
            <p className="text-sm text-gray-400">You didn't score this round before the hiring decision was made.</p>
          )
        ) : (
          <form onSubmit={scorecardForm.handleSubmit(saveScorecard)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {COMPETENCIES.map(c => (
                <div key={c.key}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{c.label}</label>
                  <div className="flex gap-1">
                    {[1, 2, 3, 4, 5].map(n => (
                      <label key={n} className={`flex-1 cursor-pointer text-center py-2 rounded-lg border text-sm font-medium transition ${
                        String(scorecardForm.watch(c.key)) === String(n) ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-gray-200 text-gray-500 hover:bg-gray-50'
                      }`}>
                        <input type="radio" {...scorecardForm.register(c.key)} value={n} className="sr-only" />
                        {n}
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Strengths</label>
              <textarea {...scorecardForm.register('strengths')} rows={2}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none resize-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Concerns</label>
              <textarea {...scorecardForm.register('concerns')} rows={2}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm focus:border-orange-500 focus:outline-none resize-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Overall Recommendation</label>
              <select {...scorecardForm.register('overall_recommendation')}
                className="w-full rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white focus:border-orange-500 focus:outline-none">
                <option value="">Select…</option>
                {Object.entries(RECOMMENDATION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>

            <button type="submit" className="bg-orange-500 text-white px-5 py-2 rounded-xl text-sm font-semibold hover:bg-orange-600">
              {myScorecard ? 'Update My Scorecard' : 'Save My Scorecard'}
            </button>
          </form>
        )}
      </Card>

    </div>
  );
}

// ─── References Tab ─────────────────────────────────────────
function ReferencesTab({ candidate, reload }) {
  const [showAdd, setShowAdd] = useState(false);
  const addForm = useForm();

  const addRef = async (data) => {
    try {
      await api.post(`/recruitment/candidates/${candidate.id}/references`, data);
      toast.success('Reference added'); setShowAdd(false); addForm.reset(); reload();
    } catch { toast.error('Failed to add'); }
  };

  const updateRef = async (refId, patch) => {
    try {
      await api.patch(`/recruitment/references/${refId}`, patch);
      reload();
    } catch { toast.error('Failed to save'); }
  };

  const sendRequest = async (refId) => {
    try {
      await api.post(`/recruitment/references/${refId}/send-request`);
      toast.success('Reference request emailed');
      reload();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to send'); }
  };

  const deleteRef = async (refId) => {
    if (!window.confirm('Remove this reference?')) return;
    try {
      await api.delete(`/recruitment/references/${refId}`);
      toast.success('Removed'); reload();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={() => setShowAdd(true)} className="inline-flex items-center gap-1 bg-orange-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-orange-600">
          <PlusIcon className="w-4 h-4" /> Add Reference
        </button>
      </div>

      {candidate.references?.length === 0 ? (
        <Card><p className="text-sm text-gray-400 text-center py-4">No references yet.</p></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {candidate.references?.map(r => (
            <div key={r.id} className="bg-white rounded-2xl border border-gray-200 p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-gray-900">{r.name}</p>
                  <p className="text-xs text-gray-500">{r.relationship || '—'}</p>
                </div>
                <button onClick={() => deleteRef(r.id)} className="text-gray-400 hover:text-red-500">
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-3 space-y-1 text-sm">
                {r.email && <p className="text-gray-600"><EnvelopeIcon className="w-3.5 h-3.5 inline mr-1" />{r.email}</p>}
                {r.phone && <p className="text-gray-600"><PhoneIcon className="w-3.5 h-3.5 inline mr-1" />{r.phone}</p>}
              </div>

              {r.contacted_at ? (
                <div className="mt-3 bg-green-50 rounded-lg p-2 text-xs text-green-700">
                  Contacted {formatDate(r.contacted_at)}{r.contacted_by_first ? ` by ${r.contacted_by_first}` : ''}
                </div>
              ) : (
                <button onClick={() => sendRequest(r.id)} disabled={!r.email}
                  className="mt-3 w-full bg-blue-50 text-blue-600 px-3 py-2 rounded-lg text-xs font-medium hover:bg-blue-100 disabled:opacity-50">
                  <PaperAirplaneIcon className="w-3.5 h-3.5 inline mr-1" />
                  {r.email ? 'Email Reference Request' : 'No email on file'}
                </button>
              )}

              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-500 mb-1">Recommendation</label>
                <select defaultValue={r.recommendation || ''}
                  onChange={(e) => updateRef(r.id, { recommendation: e.target.value || null })}
                  className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs bg-white">
                  <option value="">Not yet rated</option>
                  {Object.entries(RECOMMENDATION_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>

              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-500 mb-1">Response Notes</label>
                <textarea defaultValue={r.response_notes || ''} rows={3}
                  onBlur={(e) => e.target.value !== (r.response_notes || '') && updateRef(r.id, { response_notes: e.target.value })}
                  className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs resize-none focus:border-orange-500 focus:outline-none" />
              </div>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <Modal title="Add Reference" onClose={() => setShowAdd(false)}>
          <form onSubmit={addForm.handleSubmit(addRef)} className="space-y-3">
            <Input label="Name *" {...addForm.register('name', { required: true })} />
            <Input label="Relationship" placeholder="Former Manager, Coworker, etc." {...addForm.register('relationship')} />
            <Input label="Email" type="email" {...addForm.register('email')} />
            <Input label="Phone" {...addForm.register('phone')} />
            <button type="submit" className="w-full bg-orange-500 text-white py-2.5 rounded-xl font-semibold hover:bg-orange-600">Add Reference</button>
          </form>
        </Modal>
      )}
    </div>
  );
}

// ─── Employment File Tab ──────────────────────────────────────
//
// Shows the unified Employment File picture (same view staff sees)
// PLUS a prominent jump to the Onboarding admin workflow page where
// the per-step upload / verify / completion-date / expiration-date
// controls live. Director needs those controls; they live there
// because Onboarding is the canonical admin workflow surface.
function DocumentsTab({ candidate }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const userId = candidate.portal_user_id;

  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    setLoading(true);
    api.get(`/staff/${userId}/file`)
      .then(({ data: d }) => setData(d))
      .catch(() => toast.error('Failed to load employment file'))
      .finally(() => setLoading(false));
  }, [userId]);

  if (!userId) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-sm text-amber-800">
        The Employment File appears here once you move this candidate forward to a hire. Use the Offer / Negotiate tab to extend the conditional offer.
      </div>
    );
  }
  if (loading) return <div className="text-gray-400">Loading…</div>;
  return (
    <div className="space-y-3">
      <div className="bg-orange-50 border border-orange-200 rounded-2xl p-4 flex items-center justify-between gap-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">Manage onboarding steps</p>
          <p className="text-xs text-gray-600 mt-0.5">
            Upload documents, set completion + expiration dates, verify each step on the Onboarding workflow page.
          </p>
        </div>
        <Link to={`/onboarding?user=${userId}`}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-white bg-orange-500 hover:bg-orange-600 px-4 py-2 rounded-xl flex-shrink-0">
          Open Onboarding →
        </Link>
      </div>
      <EmployeeFile data={data} embedded canUpload uploadTargetId={Number(userId)}
        adminManageUserId={Number(userId)}
        onUploaded={() => {
          // Refresh the view after upload-to-file (writes to staff_documents)
          api.get(`/staff/${userId}/file`).then(({ data: d }) => setData(d)).catch(() => {});
        }} />
    </div>
  );
}

// ─── Background Check Tab ───────────────────────────────────
function BackgroundTab({ candidate, reload }) {
  const update = async (patch) => {
    try {
      await api.patch(`/recruitment/candidates/${candidate.id}`, patch);
      toast.success('Updated');
      reload();
    } catch { toast.error('Failed to save'); }
  };

  return (
    <Card title="Background Check" icon={ShieldCheckIcon}>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
          <select defaultValue={candidate.background_check_status || 'not_started'}
            onChange={(e) => update({ background_check_status: e.target.value })}
            className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm bg-white focus:border-orange-500 focus:outline-none">
            <option value="not_started">Not Started</option>
            <option value="requested">Requested</option>
            <option value="in_progress">In Progress</option>
            <option value="passed">Passed</option>
            <option value="failed">Failed</option>
            <option value="waived">Waived</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Requested At</label>
            <input type="date" min={DATE_FLOOR} defaultValue={candidate.background_check_requested_at?.slice(0, 10) || ''}
              onBlur={(e) => update({ background_check_requested_at: e.target.value || null })}
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-orange-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Completed At</label>
            <input type="date" min={DATE_FLOOR} defaultValue={candidate.background_check_completed_at?.slice(0, 10) || ''}
              onBlur={(e) => update({ background_check_completed_at: e.target.value || null })}
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-orange-500 focus:outline-none" />
          </div>
        </div>
        <p className="text-xs text-gray-500">For sensitive notes, use the Notes tab and tag the entry "Background Check".</p>
      </div>
    </Card>
  );
}

// ─── Offer / Negotiate Tab ──────────────────────────────────
function OfferTab({ candidate, reload }) {
  const counterForm = useForm({
    defaultValues: {
      offer_salary: candidate.offer_salary || '',
      offer_start_date: candidate.offer_start_date?.slice(0, 10) || '',
      message: '',
    },
  });

  // ─── Move Forward (Phase 4) ─────────────────────────────────────
  // New flow that replaces the old single-click "hire". Creates a
  // portal_users row in pending_clearance, generates the conditional
  // offer letter PDF, and emails the candidate a password-setup link.
  // Keep these in sync with src/utils/generateOfferLetter.js
  // DEFAULT_RESPONSIBILITIES — the backend uses its copy as source of
  // truth when generating the PDF, but the form pre-fills from here for
  // instant render (no extra fetch).
  const ROLE_DEFAULT_RESPONSIBILITIES = {
    admin: [
      'Greet families warmly at drop-off and pickup and serve as the welcoming face of BBC.',
      'Manage front-office operations including enrollment paperwork, attendance, billing, and parent communication.',
      'Support the director with scheduling, staff coordination, and licensing compliance.',
      'Maintain a clean, organized, and professional front-office environment.',
    ],
    teacher: [
      'Plan and implement age-appropriate lessons that engage and inspire your classroom.',
      'Maintain a safe, clean, and nurturing learning environment at all times.',
      'Build strong daily partnerships with families through positive, consistent communication.',
      "Collaborate with co-teachers and leadership to support each child's development.",
    ],
    float: [
      'Step in across classrooms to support teachers and maintain proper ratios.',
      "Follow each classroom's established routines and lesson plans.",
      'Build positive connections with children and families across age groups.',
      'Help with transitions, breaks, and any classroom needs as they arise.',
    ],
    cook: [
      'Prepare nutritious, child-friendly meals and snacks following BBC and CACFP guidelines.',
      'Maintain a clean, organized, and food-safe kitchen at all times.',
      'Track meal counts and inventory accurately for CACFP reporting.',
      'Coordinate with staff to handle allergies, dietary needs, and special requests.',
    ],
  };
  const defaultResponsibilities = ROLE_DEFAULT_RESPONSIBILITIES[candidate.role_applied] || ['', '', '', ''];

  const moveForwardForm = useForm({
    defaultValues: {
      offer_salary: candidate.offer_salary || '',
      intended_start_date: candidate.offer_start_date?.slice(0, 10) || '',
      first_pay_date: '',
      resp_1: defaultResponsibilities[0],
      resp_2: defaultResponsibilities[1],
      resp_3: defaultResponsibilities[2],
      resp_4: defaultResponsibilities[3],
      notes: '',
    },
  });
  const [movingForward, setMovingForward] = useState(false);
  const movedForward = !!candidate.move_forward_at;

  const doMoveForward = async (data) => {
    if (!data.offer_salary) { toast.error('Offer salary required'); return; }
    if (!window.confirm(
      'Move this candidate forward?\n\nThis will:\n• Create their portal account\n• Generate the conditional offer letter (with the responsibilities below)\n• Email them a magic link to set their password and sign the offer.'
    )) return;
    setMovingForward(true);
    try {
      const responsibilities = [data.resp_1, data.resp_2, data.resp_3, data.resp_4]
        .map(s => (s || '').trim())
        .filter(Boolean);
      const payload = {
        offer_salary: data.offer_salary,
        intended_start_date: data.intended_start_date || null,
        first_pay_date: data.first_pay_date || null,
        notes: data.notes || null,
        responsibilities: responsibilities.length === 4 ? responsibilities : undefined,
      };
      const { data: r } = await api.post(`/recruitment/candidates/${candidate.id}/move-forward`, payload);
      toast.success(r.message || 'Candidate moved forward');
      reload();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to move forward');
    } finally { setMovingForward(false); }
  };

  const resendSetup = async () => {
    if (!window.confirm('Send the candidate a fresh password-setup link?')) return;
    try {
      await api.post(`/recruitment/candidates/${candidate.id}/resend-setup-link`);
      toast.success('Setup link re-sent');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to resend');
    }
  };

  const sendCounter = async (data) => {
    try {
      await api.post(`/recruitment/candidates/${candidate.id}/counter-offer`, data);
      toast.success('Counter-offer email sent');
      counterForm.reset({ ...data, message: '' });
      reload();
    } catch { toast.error('Failed to send'); }
  };

  const setResponse = async (response) => {
    try {
      await api.post(`/recruitment/candidates/${candidate.id}/candidate-response`, { response });
      toast.success('Recorded');
      reload();
    } catch { toast.error('Failed to record'); }
  };

  return (
    <div className="space-y-6">
      {!movedForward && (
        <Card title="Move Forward — extend conditional offer" icon={PaperAirplaneIcon}>
          <p className="text-sm text-gray-600 mb-3">
            Fill in the offer details below — these populate the conditional offer letter PDF. The candidate gets a magic link to set their password and sign.
          </p>
          <form onSubmit={moveForwardForm.handleSubmit(doMoveForward)} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Pay rate (hourly) *</label>
                <input {...moveForwardForm.register('offer_salary', { required: true })} placeholder="16.00"
                  className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Start date</label>
                <input type="date" min={DATE_FLOOR} {...moveForwardForm.register('intended_start_date')}
                  className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">First pay date</label>
                <input type="date" min={DATE_FLOOR} {...moveForwardForm.register('first_pay_date')}
                  className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500" />
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">In this role, you will…</label>
              <p className="text-[11px] text-gray-500 mb-2">
                Four bullets that appear on the offer letter. Pre-filled with sensible defaults for a <strong>{formatRole(candidate.role_applied)}</strong> — edit as needed.
              </p>
              <div className="space-y-2">
                {[1, 2, 3, 4].map((n) => (
                  <textarea key={n} rows={2}
                    {...moveForwardForm.register(`resp_${n}`)}
                    placeholder={`Responsibility ${n}`}
                    className="w-full bg-white border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-orange-500 resize-y" />
                ))}
              </div>
            </div>

            <div className="flex justify-end">
              <button type="submit" disabled={movingForward}
                className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/40 text-white text-sm font-semibold px-5 py-2.5 rounded-lg inline-flex items-center gap-2">
                <PaperAirplaneIcon className="w-4 h-4" />
                {movingForward ? 'Moving forward…' : 'Move forward & send conditional offer'}
              </button>
            </div>
          </form>
        </Card>
      )}

      {movedForward && (
        <Card title="Conditional Offer Sent" icon={CheckCircleIcon}>
          <p className="text-sm text-gray-700">
            Moved forward {formatDateTime(candidate.move_forward_at)}. Candidate has their welcome email and is working through clearance.
          </p>
          <div className="flex gap-2 mt-3">
            {candidate.offer_letter_url && (
              <a href={candidate.offer_letter_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-sm text-orange-600 hover:underline">
                <DocumentTextIcon className="w-4 h-4" /> Conditional offer PDF
              </a>
            )}
            <button onClick={resendSetup}
              className="text-sm text-gray-600 hover:text-gray-800 inline-flex items-center gap-1">
              <PaperAirplaneIcon className="w-4 h-4" /> Resend setup link
            </button>
          </div>
        </Card>
      )}

      {candidate.hire_decision === 'hire' && (
        <Card title="Current Offer" icon={BanknotesIcon}>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <Field label="Salary" value={candidate.offer_salary} />
            <Field label="Start Date" value={formatDate(candidate.offer_start_date)} />
          </div>
          {candidate.offer_letter_url && (
            <a href={candidate.offer_letter_url} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-3 text-sm text-orange-600 hover:underline">
              <DocumentTextIcon className="w-4 h-4" /> View Offer Letter PDF
            </a>
          )}
        </Card>
      )}

      <Card title="Negotiation Tracking" icon={ChatBubbleLeftRightIcon}>
        <div className="grid grid-cols-3 gap-4 mb-4">
          <Stat label="Counter-offers Sent" value={candidate.counter_offer_count || 0} />
          <Stat label="Last Sent" value={formatDate(candidate.last_counter_offer_at)} />
          <Stat label="Candidate Response" value={candidate.candidate_response ? formatStage(candidate.candidate_response) : '—'} />
        </div>
        <div className="flex flex-wrap gap-2">
          {['accepted', 'declined', 'countered', 'no_response'].map(r => (
            <button key={r} onClick={() => setResponse(r)}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                candidate.candidate_response === r ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {formatStage(r)}
            </button>
          ))}
        </div>
      </Card>

      <Card title="Send Counter-Offer" icon={PaperAirplaneIcon}>
        <form onSubmit={counterForm.handleSubmit(sendCounter)} className="space-y-3">
          <Input label="Updated Salary" placeholder="$16/hr" {...counterForm.register('offer_salary')} />
          <Input label="Updated Start Date" type="date" min={DATE_FLOOR} {...counterForm.register('offer_start_date')} />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Personal Message (optional)</label>
            <textarea {...counterForm.register('message')} rows={4}
              placeholder="We've reviewed your concerns and would like to offer…"
              className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-orange-500 focus:outline-none resize-none" />
          </div>
          <button type="submit" className="bg-orange-500 text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-orange-600">
            Send Counter-Offer Email
          </button>
        </form>
      </Card>
    </div>
  );
}

// ─── Notes Tab ──────────────────────────────────────────────
function NotesTab({ candidate, reload }) {
  const { user: me } = useAuth();
  const [body, setBody] = useState('');
  const [category, setCategory] = useState('general');
  const [filter, setFilter] = useState('all');
  const [editingId, setEditingId] = useState(null);
  const [editBody, setEditBody] = useState('');

  const addNote = async () => {
    if (!body.trim()) return;
    try {
      await api.post(`/recruitment/candidates/${candidate.id}/notes`, { body, category });
      setBody(''); reload();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to add note'); }
  };

  const startEdit = (n) => { setEditingId(n.id); setEditBody(n.body); };
  const cancelEdit = () => { setEditingId(null); setEditBody(''); };
  const saveEdit = async (n) => {
    if (!editBody.trim()) return;
    try {
      await api.patch(`/recruitment/candidates/${candidate.id}/notes/${n.id}`, { body: editBody });
      setEditingId(null); setEditBody('');
      reload();
    } catch (err) { toast.error(err.response?.data?.error || 'Edit failed'); }
  };

  const deleteNote = async (noteId) => {
    if (!window.confirm('Delete this note?')) return;
    try {
      await api.delete(`/recruitment/candidates/${candidate.id}/notes/${noteId}`);
      reload();
    } catch (err) { toast.error(err.response?.data?.error || 'Failed to delete'); }
  };

  const visible = (candidate.notes || []).filter(n => filter === 'all' || n.category === filter);

  return (
    <div className="space-y-6">
      <Card title="Add Note" icon={PlusIcon}>
        <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={3}
          placeholder="Phone call, observation, follow-up — anything to remember about this candidate."
          className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-orange-500 focus:outline-none resize-none" />
        <div className="flex items-center justify-between mt-2 gap-2">
          <select value={category} onChange={(e) => setCategory(e.target.value)}
            className="rounded-xl border border-gray-200 px-3 py-2 text-sm bg-white">
            {NOTE_CATEGORIES.map(c => <option key={c.val} value={c.val}>{c.label}</option>)}
          </select>
          <button onClick={addNote} className="bg-orange-500 text-white px-5 py-2 rounded-xl text-sm font-semibold hover:bg-orange-600">
            Add Note
          </button>
        </div>
      </Card>

      <div className="flex gap-1 overflow-x-auto pb-1">
        <button onClick={() => setFilter('all')}
          className={`px-3 py-1 rounded-lg text-xs font-medium whitespace-nowrap ${filter === 'all' ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
          All ({candidate.notes?.length || 0})
        </button>
        {NOTE_CATEGORIES.map(c => {
          const count = (candidate.notes || []).filter(n => n.category === c.val).length;
          if (count === 0) return null;
          return (
            <button key={c.val} onClick={() => setFilter(c.val)}
              className={`px-3 py-1 rounded-lg text-xs font-medium whitespace-nowrap ${filter === c.val ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600'}`}>
              {c.label} ({count})
            </button>
          );
        })}
      </div>

      <div className="space-y-2">
        {visible.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">No notes yet.</p>
        ) : visible.map(n => {
          const isMine = n.author_id === me?.id;
          const canDelete = isMine || me?.role === 'director';
          const isEditing = editingId === n.id;
          return (
            <div key={n.id} className="bg-white border border-gray-200 rounded-xl p-4 group">
              <div className="flex items-start justify-between mb-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded-full bg-${NOTE_CATEGORIES.find(c => c.val === n.category)?.color || 'gray'}-100 text-${NOTE_CATEGORIES.find(c => c.val === n.category)?.color || 'gray'}-700`}>
                    {NOTE_CATEGORIES.find(c => c.val === n.category)?.label || n.category}
                  </span>
                  <span className="text-xs text-gray-500">
                    {n.author_first ? `${n.author_first} ${n.author_last || ''}` : 'System'} · {formatDateTime(n.created_at)}
                  </span>
                  {isMine && <span className="text-[10px] text-orange-600 font-medium">your note</span>}
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  {isMine && !isEditing && (
                    <button onClick={() => startEdit(n)} className="text-gray-300 hover:text-orange-500" title="Edit your note">
                      <PencilSquareIcon className="w-4 h-4" />
                    </button>
                  )}
                  {canDelete && (
                    <button onClick={() => deleteNote(n.id)} className="text-gray-300 hover:text-red-500" title={isMine ? 'Delete your note' : 'Director: delete note'}>
                      <TrashIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              {isEditing ? (
                <div className="mt-1">
                  <textarea value={editBody} onChange={(e) => setEditBody(e.target.value)}
                    rows={3} autoFocus
                    className="w-full rounded-lg border border-orange-200 px-2 py-1.5 text-sm focus:border-orange-500 focus:outline-none resize-y" />
                  <div className="flex justify-end gap-2 mt-1.5">
                    <button onClick={cancelEdit} className="text-xs text-gray-500 hover:text-gray-700 px-2">Cancel</button>
                    <button onClick={() => saveEdit(n)} disabled={!editBody.trim()}
                      className="bg-orange-500 text-white text-xs px-3 py-1 rounded-lg hover:bg-orange-600 disabled:opacity-50">
                      Save
                    </button>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{n.body}</p>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Recording + AI Notes Panel ─────────────────────────────
function RecordingPanel({ candidate, interviewId, interviewDate, zoomLink, isLocked = false }) {
  const [recordings, setRecordings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recState, setRecState] = useState('idle'); // idle, consent, recording, paused, uploading
  const [elapsed, setElapsed] = useState(0);
  const [error, setError] = useState(null);
  const mediaRecorderRef = useState({ current: null })[0];
  const chunksRef = useState({ current: [] })[0];
  const streamRef = useState({ current: null })[0];
  const startedAtRef = useState({ current: null })[0];

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/recruitment/candidates/${candidate.id}/recordings`, {
        params: interviewId ? { interview_id: interviewId } : {},
      });
      setRecordings(data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  }, [candidate.id, interviewId]);

  useEffect(() => {
    load();
    // Auto-refresh while any recording is still processing
    const id = setInterval(() => {
      const stillProcessing = recordings.some(r => ['transcribing', 'summarizing'].includes(r.status));
      if (stillProcessing) load();
    }, 15000);
    return () => clearInterval(id);
  }, [load, recordings]);

  // Cleanup mic stream on unmount
  useEffect(() => () => {
    if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
  }, [streamRef]);

  // Elapsed timer while recording
  useEffect(() => {
    if (recState !== 'recording') return;
    const id = setInterval(() => setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000)), 1000);
    return () => clearInterval(id);
  }, [recState, startedAtRef]);

  const startConsent = () => {
    setError(null);
    setRecState('consent');
  };

  const startRecording = async () => {
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error('Microphone not supported in this browser');
      }
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true },
      });
      streamRef.current = stream;
      const mime = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';
      const mr = new MediaRecorder(stream, { mimeType: mime });
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size) chunksRef.current.push(e.data); };
      mr.onstop = uploadRecording;
      mediaRecorderRef.current = mr;
      mr.start(1000); // gather chunks every 1s
      startedAtRef.current = Date.now();
      setElapsed(0);
      setRecState('recording');
    } catch (e) {
      setError(e.message);
      toast.error(e.message);
      setRecState('idle');
    }
  };

  const pauseRecording = () => {
    const mr = mediaRecorderRef.current;
    if (!mr) return;
    if (mr.state === 'recording') { mr.pause(); setRecState('paused'); }
    else if (mr.state === 'paused') { mr.resume(); setRecState('recording'); }
  };

  const stopRecording = () => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') mr.stop();
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  };

  const uploadRecording = async () => {
    setRecState('uploading');
    try {
      const blob = new Blob(chunksRef.current, { type: chunksRef.current[0]?.type || 'audio/webm' });
      const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
      const file = new File([blob], `interview-${Date.now()}.${ext}`, { type: blob.type });
      const fd = new FormData();
      fd.append('audio', file);
      fd.append('duration_seconds', String(elapsed));
      if (interviewId) fd.append('interview_id', String(interviewId));
      await api.post(`/recruitment/candidates/${candidate.id}/recordings`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Recording uploaded — transcription in progress');
      setRecState('idle');
      setElapsed(0);
      chunksRef.current = [];
      load();
    } catch (e) {
      console.error(e);
      const msg = e.response?.data?.error || 'Upload failed';
      setError(msg);
      toast.error(msg);
      setRecState('idle');
    }
  };

  const formatDur = (s) => {
    if (s == null) return '—';
    const m = Math.floor(s / 60); const r = s % 60;
    return `${m}:${String(r).padStart(2, '0')}`;
  };

  const removeRecording = async (id) => {
    if (!window.confirm('Delete this recording (and any AI notes)?')) return;
    try {
      await api.delete(`/recruitment/recordings/${id}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed to delete'); }
  };

  return (
    <Card title="Interview" icon={MicrophoneIcon}>
      {/* Schedule details inline */}
      {(interviewDate || zoomLink) && (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-3 pb-3 border-b border-gray-100 text-sm">
          {interviewDate && (
            <span className="inline-flex items-center gap-1 text-gray-700">
              <CalendarDaysIcon className="w-4 h-4 text-gray-400" />
              {formatDateTime(interviewDate)}
            </span>
          )}
          {zoomLink && (
            <a href={zoomLink} target="_blank" rel="noopener noreferrer"
              className="text-orange-500 hover:underline text-xs truncate max-w-xs">
              {zoomLink}
            </a>
          )}
        </div>
      )}

      {/* Recording controls — locked once any recording exists.
          The user explicitly wants notes/scorecard editable but a "second take"
          to be a new interview entirely (delete current recording first).  */}
      {recState === 'idle' && recordings.length === 0 && !isLocked && (
        <button onClick={startConsent}
          className="bg-red-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-red-600 inline-flex items-center gap-2">
          <MicrophoneIcon className="w-4 h-4" /> Start Recording
        </button>
      )}
      {recState === 'idle' && recordings.length === 0 && isLocked && (
        <p className="text-xs text-gray-400">No recording was made for this round.</p>
      )}
      {recState === 'idle' && recordings.length > 0 && (
        <div className="rounded-xl border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-gray-600 flex items-center gap-2">
          <MicrophoneIcon className="w-4 h-4 text-gray-400" />
          <span>An interview was recorded for this candidate. Notes + scorecards stay editable below. To record a fresh interview, delete the existing recording first.</span>
        </div>
      )}

      {recState === 'consent' && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
          <p className="text-sm text-amber-900 font-semibold">Disclose to the candidate before starting:</p>
          <p className="text-sm text-amber-900 italic">
            "We're recording this interview only to take notes. The recording is deleted after our system creates the transcript and is never shared. Is that OK with you?"
          </p>
          <p className="text-xs text-amber-700">Consent is recorded by you proceeding. Audio is auto-deleted after transcription.</p>
          <div className="flex gap-2">
            <button onClick={startRecording} className="bg-red-500 text-white px-4 py-2 rounded-xl text-sm font-medium hover:bg-red-600 inline-flex items-center gap-2">
              <MicrophoneIcon className="w-4 h-4" /> Candidate Agreed — Start
            </button>
            <button onClick={() => setRecState('idle')} className="px-4 py-2 border border-gray-200 rounded-xl text-sm text-gray-600">Cancel</button>
          </div>
        </div>
      )}

      {(recState === 'recording' || recState === 'paused') && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-center gap-3">
          <div className={`w-3 h-3 rounded-full bg-red-500 ${recState === 'recording' ? 'animate-pulse' : ''}`} />
          <span className="font-mono text-lg">{formatDur(elapsed)}</span>
          <span className="text-sm text-red-700 font-medium">{recState === 'recording' ? 'Recording…' : 'Paused'}</span>
          <div className="ml-auto flex gap-2">
            <button onClick={pauseRecording} className="bg-white border border-gray-200 px-3 py-1.5 rounded-lg text-sm inline-flex items-center gap-1">
              {recState === 'paused' ? <><PlayIcon className="w-4 h-4" /> Resume</> : <><PauseIcon className="w-4 h-4" /> Pause</>}
            </button>
            <button onClick={stopRecording} className="bg-red-500 text-white px-3 py-1.5 rounded-lg text-sm inline-flex items-center gap-1 hover:bg-red-600">
              <StopIcon className="w-4 h-4" /> Stop
            </button>
          </div>
        </div>
      )}

      {recState === 'uploading' && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-2">
          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600" />
          <span className="text-sm text-blue-700">Uploading audio…</span>
        </div>
      )}

      {error && <p className="text-xs text-red-600 mt-2">{error}</p>}

      {/* List of recordings */}
      <div className="mt-4 space-y-3">
        {loading ? (
          <p className="text-xs text-gray-400">Loading recordings…</p>
        ) : recordings.length === 0 ? (
          <p className="text-xs text-gray-400">No recordings yet.</p>
        ) : recordings.map(r => <RecordingItem key={r.id} rec={r} onDelete={() => removeRecording(r.id)} />)}
      </div>
    </Card>
  );
}

function RecordingItem({ rec, onDelete }) {
  const { isDirector } = useAuth();
  const summary = rec.ai_summary;
  return (
    <div className="border border-gray-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-2">
        <div>
          <p className="text-sm font-medium text-gray-900">
            {new Date(rec.created_at).toLocaleString()}
            <span className="text-xs text-gray-500 ml-2">
              {rec.duration_seconds ? `${Math.floor(rec.duration_seconds / 60)}:${String(rec.duration_seconds % 60).padStart(2, '0')}` : ''}
            </span>
          </p>
          <p className="text-xs text-gray-500">
            By {rec.recorded_by_first ? `${rec.recorded_by_first} ${rec.recorded_by_last || ''}` : 'Unknown'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {rec.task_count > 0 && (
            <Link to="/tasks" className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium hover:bg-blue-100 inline-flex items-center gap-1">
              <SparklesIcon className="w-3 h-3" />
              {rec.open_task_count}/{rec.task_count} task{rec.task_count === 1 ? '' : 's'}
            </Link>
          )}
          <RecordingStatusBadge status={rec.status} />
          {isDirector && (
            <button onClick={onDelete} title="Delete recording (director only)"
              className="text-gray-400 hover:text-red-500"><TrashIcon className="w-4 h-4" /></button>
          )}
        </div>
      </div>

      {rec.error_message && <p className="text-xs text-red-600 mt-2">{rec.error_message}</p>}

      {summary && (
        <div className="mt-3 space-y-3 text-sm">
          <div className="bg-orange-50 border border-orange-100 rounded-lg p-3">
            <div className="flex items-center gap-1 text-xs font-semibold text-orange-700 mb-1">
              <SparklesIcon className="w-3.5 h-3.5" /> AI Summary
            </div>
            <p className="text-gray-800">{summary.summary}</p>
            {summary.suggested_rating && (
              <p className="text-xs text-orange-700 mt-2">
                Suggested: <strong>{summary.suggested_rating.label?.replace(/_/g, ' ')}</strong>
                {summary.suggested_rating.score ? ` (${summary.suggested_rating.score}/5)` : ''}
                {summary.suggested_rating.rationale ? ` — ${summary.suggested_rating.rationale}` : ''}
              </p>
            )}
          </div>

          <SummarySection title="Key Points" items={summary.key_points} />
          <SummarySection title="Strengths" items={summary.strengths} color="green" />
          <SummarySection title="Concerns" items={summary.concerns} color="red" />
          <SummarySection title="Follow-ups" items={summary.followups} color="blue" />
        </div>
      )}

      {rec.transcript_text && (
        <details className="mt-3">
          <summary className="text-xs text-gray-500 cursor-pointer hover:text-gray-700">View full transcript</summary>
          <pre className="text-xs text-gray-700 whitespace-pre-wrap mt-2 max-h-64 overflow-y-auto bg-gray-50 rounded-lg p-3">{rec.transcript_text}</pre>
        </details>
      )}
    </div>
  );
}

function SummarySection({ title, items, color = 'gray' }) {
  if (!items?.length) return null;
  const colors = {
    gray: 'text-gray-700',
    green: 'text-green-700',
    red: 'text-red-700',
    blue: 'text-blue-700',
  };
  return (
    <div>
      <p className={`text-xs font-semibold uppercase ${colors[color]} mb-1`}>{title}</p>
      <ul className="space-y-1 pl-4 list-disc">
        {items.map((it, i) => <li key={i} className="text-xs text-gray-700">{it}</li>)}
      </ul>
    </div>
  );
}

function RecordingStatusBadge({ status }) {
  const styles = {
    transcribing: 'bg-blue-100 text-blue-700',
    summarizing: 'bg-purple-100 text-purple-700',
    complete: 'bg-green-100 text-green-700',
    failed: 'bg-red-100 text-red-700',
    uploading: 'bg-amber-100 text-amber-700',
  };
  const labels = {
    transcribing: 'Transcribing…',
    summarizing: 'AI Summary…',
    complete: 'Complete',
    failed: 'Failed',
    uploading: 'Uploading…',
  };
  return (
    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${styles[status] || 'bg-gray-100 text-gray-600'}`}>
      {labels[status] || status}
    </span>
  );
}

// ─── Shared bits ────────────────────────────────────────────

// Sends a "you're X of Y done, here's what's next" reminder email to
// the candidate. Phase 3 of the consolidation. Best-effort — the
// backend is fire-and-forget, so even if email infra is down, the
// click feels instant.
// Report what the nudge endpoint ACTUALLY did, 2026-09-14.
//
// These buttons used to toast "Reminder sent" on any 2xx. The endpoint
// answered `{ ok: true }` without awaiting delivery, so "sent" was shown
// for mail that was suppressed, skipped as already-complete, addressed to
// a user with no email, or failed outright.
//
//   THE TOAST WAS NOT REPORTING THE SEND. IT WAS REPORTING THAT THE
//   REQUEST ARRIVED.
//
// The route now awaits the mailer and returns { sent, reason, message }.
// A 503 means notices are held — that is a deliberate state, not a
// failure, so it gets a plain explanation rather than a red error.
function nudgeToast(data) {
  if (data?.sent) return toast.success('Reminder sent');
  return toast(data?.message || 'Nothing was sent.', { icon: 'ℹ️' });
}
function nudgeErrorToast(err) {
  const status = err?.response?.status;
  const body = err?.response?.data || {};
  if (status === 503) return toast(body.error || 'Notices are on hold — nothing was sent.', { icon: '⏸️' });
  return toast.error(body.error || 'Nudge failed');
}

function NudgeButton({ userId, phase }) {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/staff/${userId}/nudge`, { phase });
      nudgeToast(data);
    } catch (err) {
      nudgeErrorToast(err);
    } finally { setBusy(false); }
  };
  return (
    <button onClick={click} disabled={busy}
      className="text-xs font-semibold text-orange-700 hover:text-orange-800 inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-orange-200 hover:bg-orange-50 disabled:opacity-50">
      {busy ? 'Sending…' : 'Nudge'}
    </button>
  );
}

// Header-styled Nudge button — same backend as the per-phase one,
// but visible in the top-right of the candidate detail header so
// director doesn't have to drill into "Clearance & Onboarding" to
// find it. No phase override → notifyProgressReminder picks the
// appropriate phase internally based on the candidate's current state.
function CandidateHeaderNudgeButton({ userId }) {
  const [busy, setBusy] = useState(false);
  const click = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/staff/${userId}/nudge`, { phase: 'phase_1' });
      nudgeToast(data);
    } catch (err) {
      nudgeErrorToast(err);
    } finally { setBusy(false); }
  };
  return (
    <button onClick={click} disabled={busy}
      className="inline-flex items-center gap-1 text-xs font-medium text-orange-700 hover:text-orange-800 border border-orange-200 hover:bg-orange-50 rounded-lg px-2.5 py-1 disabled:opacity-40"
      title="Email this candidate a 'here's what's left on your checklist' reminder.">
      <ChatBubbleLeftRightIcon className="w-3.5 h-3.5" />
      {busy ? 'Sending…' : 'Nudge'}
    </button>
  );
}

function Card({ title, icon: Icon, action, className = '', children }) {
  return (
    <div className={`bg-white rounded-2xl border border-gray-200 p-5 shadow-sm ${className}`}>
      {title && (
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {Icon && <Icon className="w-4 h-4 text-gray-400" />}
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
          </div>
          {action}
        </div>
      )}
      {children}
    </div>
  );
}

function Field({ label, value }) {
  return (
    <div className="py-1.5">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-sm text-gray-900">{value || '—'}</p>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-xl p-3">
      <p className="text-xs text-gray-500">{label}</p>
      <p className="text-base font-semibold text-gray-900 mt-1">{value}</p>
    </div>
  );
}

function Input({ label, ...props }) {
  return (
    <div>
      {label && <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>}
      <input {...props}
        className="w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:border-orange-500 focus:outline-none" />
    </div>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-gray-100 sticky top-0 bg-white">
          <h2 className="text-lg font-semibold">{title}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="p-5">{children}</div>
      </div>
    </div>
  );
}

// ─── Scorecard helpers ──────────────────────────────────────

function ScorecardReadOnly({ sc, isAi, canDelete, onDelete }) {
  const author = isAi ? 'AI baseline' : `${sc.scored_by_first || 'Unknown'} ${sc.scored_by_last || ''}`.trim();
  const ratings = COMPETENCIES.map(c => ({ ...c, value: sc[c.key] }));
  const avg = (() => {
    const present = ratings.filter(r => r.value != null).map(r => Number(r.value));
    return present.length === 0 ? null : (present.reduce((a, b) => a + b, 0) / present.length).toFixed(1);
  })();
  return (
    <div className={`rounded-xl border p-3 ${isAi ? 'border-orange-200 bg-orange-50/30' : 'border-gray-100 bg-white'}`}>
      <div className="flex items-start justify-between mb-2">
        <div>
          <p className="text-sm font-semibold text-gray-900 inline-flex items-center gap-1.5">
            {isAi && <SparklesIcon className="w-4 h-4 text-orange-500" />}
            {author}
            {sc.overall_recommendation && (
              <span className="ml-2 text-xs font-medium px-2 py-0.5 rounded-full bg-amber-50 text-amber-700">
                {RECOMMENDATION_LABELS[sc.overall_recommendation] || sc.overall_recommendation}
              </span>
            )}
          </p>
          <p className="text-[11px] text-gray-400">
            {sc.scored_at && `Scored ${formatDateTime(sc.scored_at)}`}
            {avg != null && <span className="ml-2 font-semibold text-gray-600">Avg {avg}</span>}
          </p>
        </div>
        {canDelete && (
          <button onClick={onDelete} className="text-xs text-gray-400 hover:text-red-500 inline-flex items-center gap-1">
            <TrashIcon className="w-3.5 h-3.5" /> Delete
          </button>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-x-4 gap-y-1.5 text-xs">
        {ratings.map(r => (
          <div key={r.key} className="flex items-center justify-between">
            <span className="text-gray-600">{r.label}</span>
            <span className={`font-semibold ${r.value == null ? 'text-gray-300' : 'text-gray-900'}`}>
              {r.value == null ? '—' : `${r.value}/5`}
            </span>
          </div>
        ))}
      </div>
      {(sc.strengths || sc.concerns || sc.ai_summary || sc.ai_red_flags) && (
        <div className="mt-3 pt-3 border-t border-gray-100 space-y-1.5 text-xs">
          {sc.ai_summary && <p><span className="font-semibold text-gray-700">Summary:</span> <span className="text-gray-600">{sc.ai_summary}</span></p>}
          {sc.strengths && <p><span className="font-semibold text-green-700">Strengths:</span> <span className="text-gray-600">{sc.strengths}</span></p>}
          {sc.concerns && <p><span className="font-semibold text-amber-700">Concerns:</span> <span className="text-gray-600">{sc.concerns}</span></p>}
          {sc.ai_red_flags && <p><span className="font-semibold text-red-700">Red flags:</span> <span className="text-gray-600">{sc.ai_red_flags}</span></p>}
        </div>
      )}
    </div>
  );
}

function SummaryRow({ scorecards }) {
  const all = [];
  for (const sc of scorecards) {
    for (const c of COMPETENCIES) {
      if (sc[c.key] != null) all.push(Number(sc[c.key]));
    }
  }
  if (all.length === 0) return null;
  const avg = (all.reduce((a, b) => a + b, 0) / all.length).toFixed(2);
  return (
    <div className="rounded-xl border border-orange-100 bg-orange-50/40 p-3 text-xs flex items-center justify-between">
      <span className="font-semibold text-orange-900">Combined human average across {scorecards.length} evaluator{scorecards.length === 1 ? '' : 's'}</span>
      <span className="font-bold text-orange-900">{avg} / 5</span>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Clearance & Onboarding tab (Phase 4) — admin control surface for the post-
// move-forward workflow. Wires the onboardingStatusController endpoints to
// per-step action buttons.
// ─────────────────────────────────────────────────────────────────────────────

const STATUS_PILL = {
  pending:   'bg-gray-100 text-gray-600',
  completed: 'bg-blue-100 text-blue-700',
  verified:  'bg-green-100 text-green-700',
};

function ClearanceTab({ candidate }) {
  const userId = candidate.portal_user_id;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scheduleEdit, setScheduleEdit] = useState({ start_date: '', first_training_day: '' });

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [profileRes, onboardingRes] = await Promise.all([
        api.get('/staff').then(r => r.data.find(u => u.id === userId)),
        api.get(`/onboarding/${userId}`),
      ]);
      setData({ user: profileRes, steps: onboardingRes.data });
      setScheduleEdit({
        start_date: profileRes?.start_date?.slice(0, 10) || '',
        first_training_day: profileRes?.first_training_day?.slice(0, 10) || '',
      });
    } catch (err) {
      toast.error('Failed to load clearance data');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  if (!userId) {
    return (
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
        Candidate has not been moved forward yet. Use the Offer tab to extend the conditional offer first.
      </div>
    );
  }
  if (loading || !data) return <div className="text-gray-400">Loading clearance…</div>;

  const phase1 = data.steps.filter(s => s.phase === 'phase_1' || (!s.phase && (s.step_order < 10))).sort((a, b) => a.step_order - b.step_order);
  const phase2 = data.steps.filter(s => s.phase === 'phase_2' || (!s.phase && s.step_order >= 10)).sort((a, b) => a.step_order - b.step_order);
  const status = data.user?.status;
  const counts = {
    p1_total: phase1.length,
    p1_verified: phase1.filter(s => s.status === 'verified').length,
    p2_total: phase2.length,
    p2_verified: phase2.filter(s => s.status === 'verified').length,
  };

  const saveScheduleDates = async () => {
    try {
      await api.patch(`/portal-users/${userId}/schedule-dates`, {
        start_date: scheduleEdit.start_date || null,
        first_training_day: scheduleEdit.first_training_day || null,
      });
      toast.success('Schedule dates updated');
      load();
    } catch (err) {
      toast.error('Failed to update schedule dates');
    }
  };

  // Director-only manual promotion. The auto-promote in
  // recomputeUserStatus() only flips on full Phase 2 verification; this
  // bypass covers the case where paperwork is done in real life but
  // some steps haven't been verified in the portal yet.
  const markActive = async () => {
    const verifiedCount = counts.p2_verified;
    const totalCount = counts.p2_total;
    const remaining = totalCount - verifiedCount;
    const msg = remaining > 0
      ? `Mark this staff member Active even though ${remaining} of ${totalCount} Phase 2 step${totalCount === 1 ? '' : 's'} ${remaining === 1 ? 'is' : 'are'} not yet verified? You should still verify the remaining steps when the paperwork is in.`
      : 'Mark this staff member Active?';
    if (!window.confirm(msg)) return;
    try {
      await api.post(`/staff/${userId}/mark-active`);
      toast.success('Marked Active');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to mark active');
    }
  };
  const canMarkActive = status && ['onboarding', 'pending_hire', 'pending_clearance'].includes(status);

  return (
    <div className="space-y-6">
      {/* Status + schedule dates */}
      <Card title="Status" icon={ShieldCheckIcon}>
        <div className="grid grid-cols-3 gap-4 text-sm mb-4">
          <Stat label="Current status" value={
            <div className="flex items-center gap-2 flex-wrap">
              <StatusPill status={status} />
              {canMarkActive && (
                <button onClick={markActive}
                  title="Director override: flip status to Active so this staff appears on the team schedule and counts in payroll."
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-green-700 hover:text-green-800 px-2 py-0.5 rounded-md border border-green-300 bg-green-50 hover:bg-green-100">
                  Mark Active →
                </button>
              )}
            </div>
          } />
          <Stat label="Phase 1 clearance" value={`${counts.p1_verified} / ${counts.p1_total} verified`} />
          <Stat label="Phase 2 onboarding" value={`${counts.p2_verified} / ${counts.p2_total} verified`} />
        </div>
        <div className="grid grid-cols-3 gap-3 items-end pt-3 border-t border-gray-100">
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">Start date</label>
            <input type="date" min={DATE_FLOOR} value={scheduleEdit.start_date}
              onChange={(e) => setScheduleEdit(prev => ({ ...prev, start_date: e.target.value }))}
              className="w-full bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-orange-500" />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-700 mb-1">First training day</label>
            <input type="date" min={DATE_FLOOR} value={scheduleEdit.first_training_day}
              onChange={(e) => setScheduleEdit(prev => ({ ...prev, first_training_day: e.target.value }))}
              className="w-full bg-white border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-orange-500" />
          </div>
          <button onClick={saveScheduleDates}
            className="bg-orange-500 hover:bg-orange-600 text-white text-sm font-medium px-4 py-1.5 rounded-lg">
            Save dates
          </button>
        </div>
        <p className="text-xs text-gray-500 mt-2">
          When all Phase 1 is verified <em>and</em> start date is set, status auto-flips to Onboarding (and probation_end_date is set to start_date + 60 days when onboarding → active fires).
        </p>
      </Card>

      <Card title={`Phase 1 — Pre-start Clearance (${counts.p1_verified}/${counts.p1_total})`} icon={ClipboardDocumentListIcon}
            action={<NudgeButton userId={userId} phase="phase_1" />}>
        <div className="space-y-2">
          {phase1.map(s => <StepRow key={s.id} step={s} userId={userId} onChanged={load} />)}
        </div>
      </Card>

      <Card title={`Phase 2 — Onboarding (${counts.p2_verified}/${counts.p2_total})`} icon={ClipboardDocumentListIcon}
            action={<NudgeButton userId={userId} phase="phase_2" />}>
        <div className="space-y-2">
          {phase2.map(s => <StepRow key={s.id} step={s} userId={userId} onChanged={load} />)}
        </div>
      </Card>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    pending_clearance: { label: 'Pending clearance', cls: 'bg-blue-100 text-blue-700' },
    pending_hire:      { label: 'Pending hire',       cls: 'bg-blue-100 text-blue-700' },
    onboarding:        { label: 'Onboarding',         cls: 'bg-green-100 text-green-700' },
    active:            { label: 'Active',             cls: 'bg-green-100 text-green-700' },
  };
  const m = map[status] || { label: status || '—', cls: 'bg-gray-100 text-gray-700' };
  return <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full ${m.cls}`}>{m.label}</span>;
}

function StepRow({ step, userId, onChanged }) {
  // The /onboarding/:userId endpoint returns rows with both progress + template
  // fields flattened. Cope with either shape.
  const progressId = step.progress_id || step.id;
  const stepName = step.step_name;
  const description = step.description;
  const stepStatus = step.status || 'pending';
  const adminOwned = step.uploader === 'admin';
  const isBackground = step.step_category === 'background';
  const isIlead = isBackground && step.step_name?.toLowerCase().includes('ilead');
  const isFingerprint = isBackground && !isIlead;
  const verified = stepStatus === 'verified';

  const [busy, setBusy] = useState(false);
  const [bypassDate, setBypassDate] = useState('');
  const [adminCompletionDate, setAdminCompletionDate] = useState('');
  const [adminExpirationDate, setAdminExpirationDate] = useState('');
  const [showBypass, setShowBypass] = useState(false);
  const needsExpiration = !!step.requires_expiration_date;
  const fileInputRef = useRef(null);

  // Explicit click handler — bypass <label for hidden input> which mobile
  // browsers handle inconsistently. The hidden <input type="file"> is
  // mounted in the JSX below; this opens it programmatically.
  const openFilePicker = () => {
    if (needsExpiration && !adminExpirationDate) {
      toast.error(`Enter the expiration date first (valid ${step.validity_months || ''} months from completion).`);
      return;
    }
    fileInputRef.current?.click();
  };

  const verify = async () => {
    if (needsExpiration && !adminExpirationDate && !step.expiration_date) {
      toast.error('Enter the expiration date before verifying.');
      return;
    }
    setBusy(true);
    try {
      const body = {};
      if (adminExpirationDate) body.expiration_date = adminExpirationDate;
      // Phase 1.5: unified verify endpoint, audit logged.
      const { data } = await api.post(
        `/file/${userId}/items/onboarding_progress/${progressId}/verify`, body);
      if (data?.transitioned) toast.success(`Status updated → ${data.status.replace(/_/g, ' ')}`);
      else toast.success('Step verified');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to verify');
    } finally { setBusy(false); }
  };

  const adminUpload = async (e) => {
    const file = e.target.files?.[0];
    // Always reset the input value so the same file can be re-picked.
    e.target.value = '';
    if (!file) return;
    if (needsExpiration && !adminExpirationDate) {
      toast.error(`Enter the expiration date — this step requires one (${step.validity_months || 'N'} months from completion).`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('document', file);
      fd.append('template_id', String(step.template_id));
      if (adminCompletionDate) fd.append('completion_date', adminCompletionDate);
      if (adminExpirationDate) fd.append('expiration_date', adminExpirationDate);
      // Phase 1.5: route admin uploads through the unified controller
      // so file_audit_log captures the action regardless of surface.
      const { data } = await api.post(`/file/${userId}/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (data?.transitioned) toast.success(`Uploaded + status → ${data.status.replace(/_/g, ' ')}`);
      else toast.success('Result uploaded and verified');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally { setBusy(false); }
  };

  const bypassFingerprint = async () => {
    if (!bypassDate) { toast.error('Enter the date of the prior fingerprinting'); return; }
    setBusy(true);
    try {
      const { data } = await api.patch(`/onboarding/${userId}/steps/${progressId}/bypass-fingerprint`, {
        last_fingerprint_date: bypassDate,
      });
      if (data?.transitioned) toast.success(`Bypassed + status → ${data.status.replace(/_/g, ' ')}`);
      else toast.success('Fingerprint bypass recorded');
      setShowBypass(false);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Bypass failed');
    } finally { setBusy(false); }
  };

  const initiateIlead = async () => {
    if (!window.confirm('Mark iLEAD consent as initiated? This means you\'ve invited the candidate via the iLEAD childcare portal and the State background check is now in motion. Status will flip to "pending hire".')) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/onboarding/${userId}/ilead-initiate`, { progress_id: progressId });
      if (data?.transitioned) toast.success(`Initiated + status → ${data.status.replace(/_/g, ' ')}`);
      else toast.success('iLEAD consent marked initiated');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <div className={`border rounded-xl p-3 ${verified ? 'border-green-200 bg-green-50/30' : 'border-gray-200 bg-white'}`}>
      <div className="flex items-start gap-3">
        <div className="flex-shrink-0 mt-0.5">
          {verified
            ? <CheckCircleIcon className="w-5 h-5 text-green-500" />
            : <div className="w-5 h-5 rounded-full border-2 border-gray-300" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="font-medium text-gray-900 text-sm">{stepName}</div>
            <div className="flex items-center gap-2">
              <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_PILL[stepStatus] || STATUS_PILL.pending}`}>
                {stepStatus}
              </span>
              {adminOwned && <span className="text-xs text-gray-400">admin</span>}
            </div>
          </div>
          <p className="text-xs text-gray-600 leading-snug">{description}</p>

          {/* External link, when relevant */}
          {step.external_url && (
            <a href={step.external_url} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-xs text-orange-600 hover:underline mt-2">
              <ArrowRightIcon className="w-3.5 h-3.5" /> Open external site
            </a>
          )}

          {/* Already-uploaded doc */}
          {step.document_url && (
            <a href={step.document_url} target="_blank" rel="noreferrer"
              className="ml-3 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline mt-2">
              <DocumentTextIcon className="w-3.5 h-3.5" /> View uploaded doc
            </a>
          )}

          {/* Action panel — varies by step type. Admin can upload on ANY
              step (their own + override candidate-owned). Fingerprint adds
              a bypass button. iLEAD adds the "mark invite sent" button.
              Anything with requires_expiration_date forces an expiration
              date before the file/verify goes through. */}
          {!verified && (
            <div className="mt-3 space-y-2">
              <div className="flex flex-wrap gap-2 items-center">
                <input type="date" min={DATE_FLOOR} max={completionMax()}
                  value={adminCompletionDate}
                  onChange={(e) => setAdminCompletionDate(e.target.value)}
                  title="Completion date"
                  className="bg-white border border-gray-200 rounded-lg px-2 py-1 text-xs"
                />
                {needsExpiration && (
                  <input type="date" min={DATE_FLOOR} max={expiryMax()}
                    value={adminExpirationDate}
                    onChange={(e) => setAdminExpirationDate(e.target.value)}
                    title={`Expiration date — required, valid for ${step.validity_months || ''} months`}
                    className="bg-amber-50 border border-amber-300 rounded-lg px-2 py-1 text-xs"
                    placeholder="Exp. date"
                  />
                )}
                <button type="button" onClick={openFilePicker} disabled={busy}
                  className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-500/40 text-white text-xs font-medium px-3 py-1.5 rounded-lg">
                  {busy ? 'Uploading…' : (adminOwned ? 'Upload result' : 'Upload doc')}
                </button>
                <input ref={fileInputRef} type="file"
                  accept="image/*,application/pdf"
                  style={{ display: 'none' }}
                  onChange={adminUpload} />

                {/* Verify button only when the step is already "completed" */}
                {stepStatus === 'completed' && (
                  <button onClick={verify} disabled={busy}
                    className="bg-green-500 hover:bg-green-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg">
                    Verify
                  </button>
                )}
              </div>

              {/* Fingerprint-specific: bypass with prior prints */}
              {isFingerprint && (
                <div className="flex flex-wrap gap-2 items-center">
                  {!showBypass && (
                    <button onClick={() => setShowBypass(true)}
                      className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-xs font-medium px-3 py-1.5 rounded-lg">
                      Bypass — valid prints within 3 yrs
                    </button>
                  )}
                  {showBypass && (
                    <>
                      <input type="date" min={DATE_FLOOR} max={completionMax()} value={bypassDate}
                        onChange={(e) => setBypassDate(e.target.value)}
                        title="Date of prior fingerprinting"
                        className="bg-white border border-gray-200 rounded-lg px-2 py-1 text-xs" />
                      <button onClick={bypassFingerprint} disabled={busy}
                        className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg">
                        Confirm bypass
                      </button>
                      <button onClick={() => setShowBypass(false)} className="text-xs text-gray-500">
                        Cancel
                      </button>
                    </>
                  )}
                </div>
              )}

              {/* iLEAD-specific: mark invite sent (in addition to the upload
                  affordance above for the state-approval doc that comes back). */}
              {isIlead && stepStatus === 'pending' && (
                <div>
                  <button onClick={initiateIlead} disabled={busy}
                    className="bg-blue-500 hover:bg-blue-600 text-white text-xs font-medium px-3 py-1.5 rounded-lg">
                    Mark iLEAD invite sent
                  </button>
                </div>
              )}

              {needsExpiration && (
                <p className="text-[11px] text-amber-700">
                  This step requires an expiration date (valid {step.validity_months} months from completion).
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
